import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, gql } from '@apollo/client'
import { validateFunc } from '../../../constraints/constraints'
import { stripeCurrencies } from '../../../config/currencies'
import { saveCurrencyConfiguration } from '../../../apollo'
import {
  Box, Typography, Button, Select, MenuItem, Alert, CircularProgress, FormControl
} from '@mui/material'
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange'

const SAVE_CURRENCY_CONFIGURATION = gql`${saveCurrencyConfiguration}`

const labelSx = { fontSize: '0.78rem', fontWeight: 700, color: '#374151', mb: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }
const selectSx = { borderRadius: '10px', fontSize: '0.875rem' }

function Currency(props) {
  const [currencyCode, setCurrencyCode] = useState(props.currencyCode || '')
  const [currencySymbol, setCurrencySymbol] = useState(props.currencySymbol || '')
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  const [mutate, { loading }] = useMutation(SAVE_CURRENCY_CONFIGURATION, {
    onCompleted: () => {
      setSuccess('Currency configuration saved!')
      setError('')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: () => {
      setError('Failed to save configuration.')
      setSuccess('')
      setTimeout(() => setError(''), 3000)
    }
  })

  const handleSave = () => {
    const codeOk = !validateFunc({ currencyCode }, 'currencyCode')
    const symOk = !validateFunc({ currencySymbol }, 'currencySymbol')
    if (!codeOk || !symOk) {
      setError('Please select both currency code and symbol')
      setTimeout(() => setError(''), 3000)
      return
    }
    mutate({ variables: { configurationInput: { currency: currencyCode, currencySymbol } } })
  }

  return (
    <Box sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #3b1f00 60%, #92400e 100%)', px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CurrencyExchangeIcon sx={{ color: '#fcd34d', fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>Currency</Typography>
      </Box>

      <Box sx={{ p: 2.5, bgcolor: '#fff' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <Box>
            <Typography sx={labelSx}>Currency Code</Typography>
            <FormControl fullWidth size="small">
              <Select value={currencyCode} onChange={e => setCurrencyCode(e.target.value)} displayEmpty sx={selectSx}>
                <MenuItem value="" disabled><Typography sx={{ color: '#94a3b8', fontSize: '0.875rem' }}>Select code</Typography></MenuItem>
                {stripeCurrencies.map(val => (
                  <MenuItem key={val.currency} value={val.currency} sx={{ fontSize: '0.875rem' }}>{val.currency}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography sx={labelSx}>Currency Symbol</Typography>
            <FormControl fullWidth size="small">
              <Select value={currencySymbol} onChange={e => setCurrencySymbol(e.target.value)} displayEmpty sx={selectSx}>
                <MenuItem value="" disabled><Typography sx={{ color: '#94a3b8', fontSize: '0.875rem' }}>Select symbol</Typography></MenuItem>
                {stripeCurrencies.map(val => (
                  <MenuItem key={val.currencySymbol} value={val.currencySymbol} sx={{ fontSize: '0.875rem' }}>{val.currencySymbol}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {success && <Alert severity="success" sx={{ mb: 1.5, borderRadius: '10px', fontSize: '0.82rem' }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: '10px', fontSize: '0.82rem' }}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            disabled={loading}
            onClick={handleSave}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3, bgcolor: '#92400e', '&:hover': { bgcolor: '#78350f' } }}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default withTranslation()(Currency)